/* header-tabbed.js — behaviour for the tabbed header (sections/header.liquid,
 * compiled path). Two jobs:
 *
 *  1. HUB NAV. Hovering / focusing a hub tab swaps the tier-2 band to that
 *     hub while the pointer (or focus) is in the header; leaving returns the
 *     band to the PAGE'S hub (data-hub-rest, resolved by hubnav.liquid —
 *     Luke 2026-09-08 23:12: the active tab must be the one the page is
 *     under, not always Bathroom). Tab and category clicks and panel opens
 *     remember the hub in sessionStorage st.hub for pages that map to no
 *     hub. Hovering / focusing a category opens its
 *     tier-3 panel with the same hover-intent set the old megamenu controller
 *     in theme.js used — open delay, sibling-switch settle, close grace on
 *     leaving the header, Escape closes. theme.js's controller keys off
 *     `.site-header .nav`, which this header does not render, so it returns
 *     early and the two never fight. Keyboard: roving tabindex + arrows on
 *     the tab strip, ArrowDown from a tab into its band, ArrowDown from a
 *     category into its panel, Escape back to the category link. Coarse
 *     pointers (touch): the first tap on a category OPENS the panel, the
 *     second follows the link.
 *
 *  2. DELIVER-TO chip. Stores the shopper's postcode in localStorage
 *     (st.postcode) and, through assets/locality.js, the suburb it resolved
 *     to (st.locality) — the chip reads "Lane Cove 2064", or the bare
 *     postcode when nothing named it. Exposes
 *     window.SydneyTaps.postcode.{get,set(pc, locality?),clear,locality}
 *     and fires `st:postcode` ({postcode, locality}) on
 *     document so shipping estimates (PDP, cart) can hang off one source.
 *     Also pre-fills an empty [data-postcode] field (snippets/postcode-eta)
 *     when the page has one.
 *     It does NOT own first paint any more: snippets/locality-boot.liquid, an
 *     inline parser-blocking script rendered inside .deliver, paints the label
 *     from the same localStorage record before the browser's first frame, so
 *     the chip never shows the prompt to a shopper who has chosen a place.
 *     Everything here compares before it writes and is a no-op on boot.
 *
 *  3. SCROLL BEHAVIOUR (header_scroll_mode, 2026-09-08) for every mode but
 *     hide-all (theme.js keeps that one). See the CSS block "scroll
 *     behaviour" in header-tabbed.css for the classes and properties this
 *     drives. Mechanics: an IntersectionObserver on a body-level sentinel
 *     the height of topbar + bar decides PINNED (the strip has reached the
 *     viewport top; the strip keeps its tabs, 44px instead of 54) — no scroll
 *     maths for the threshold — and a single rAF
 *     scroll handler decides direction only: scrolling UP more than 120px in
 *     one run, or a flick faster than 1.2px/ms, REVEALS the bar above the
 *     pinned strip; 24px back down tucks it away. Nothing changes while a
 *     panel, the search dialog, a flyout, the drawer or the band peek is
 *     open, or while focus is in the bar (a hidden bar never holds focus:
 *     focusin reveals it). Programmatic jumps (> half a viewport, e.g. the
 *     PLP island's scrollIntoView on every refinement) are ignored.
 *     --header-h is published from the rows that are actually pinned.
 */
(function () {
  'use strict';
  var header = document.querySelector('.site-header--tabbed');
  if (!header) return;

  /* ---------- 1. hub nav ------------------------------------------------ */
  var nav = header.querySelector('[data-hubnav]');
  if (nav) {
    var tabs = Array.prototype.slice.call(nav.querySelectorAll('[data-hub-tab]'));
    // condensed-row hub links (snippets/hubnav.liquid): plain links that
    // mirror the tabs' hover/focus band swap without joining the tablist
    var minis = Array.prototype.slice.call(nav.querySelectorAll('[data-hub-mini]'));
    var bands = Array.prototype.slice.call(nav.querySelectorAll('[data-hub-band]'));
    var cats = Array.prototype.slice.call(nav.querySelectorAll('[data-hub-cat]'));
    var coarse = window.matchMedia ? window.matchMedia('(hover:none)') : { matches: false };
    // SETTLE was 140ms: the hover-intent wait before an ALREADY-OPEN panel is
    // replaced by the one under the pointer. Luke 2026-09-14 wants to "move
    // through the menuitems smoothly but quickly see what's in the megamenu
    // section", and 140 + a 160ms fade put the new panel a third of a second
    // behind the pointer. 70ms still filters a pass-through (the callback
    // re-tests :hover before it commits), and the swap below is interruptible,
    // so an early commit costs nothing.
    var OPEN_DELAY = 120, CLOSE_GRACE = 240, SETTLE = 70, TAB_DELAY = 80;
    // panel swap (see the "tier-2 -> tier-2 SWAP" block in header-tabbed.css):
    // SWAP_IN = the arriving panel's fade, SWAP_H = the container's height
    // morph, CLOSE_MS = the base close fade the clamp has to outlive
    var SWAP_IN = 130, SWAP_H = 170, CLOSE_MS = 170;
    var openCat = null, openTimer = null, closeTimer = null, switchTimer = null, tabTimer = null;

    // the page's own hub: server-resolved, or the session's last hub applied
    // by the inline script under the nav — read AFTER both ran (deferred)
    var restTab = nav.querySelector('[data-hub-tab].is-active');
    var restKey = nav.getAttribute('data-hub-rest') || (restTab ? restTab.getAttribute('data-hub-tab') : null);
    function remember(key) {
      if (!key) return;
      try { sessionStorage.setItem('st.hub', key); } catch (e) { /* private mode */ }
    }
    function hubOf(el) {
      if (!el || !el.closest) return null;
      var band = el.closest('[data-hub-band]');
      if (band) return band.getAttribute('data-hub-band');
      var tab = el.closest('[data-hub-tab],[data-hub-mini]');
      return tab ? (tab.getAttribute('data-hub-tab') || tab.getAttribute('data-hub-mini')) : null;
    }
    function returnHome() {
      var on = nav.querySelector('[data-hub-tab].is-active');
      if (restKey && (!on || on.getAttribute('data-hub-tab') !== restKey)) showHub(restKey);
    }
    function linkOf(li) { return li.querySelector('.hubnav__cat-link'); }
    function panelOf(li) { return li.querySelector('[data-hub-panel]'); }
    function activeBand() { for (var i = 0; i < bands.length; i++) if (!bands[i].hidden) return bands[i]; return null; }

    /* ---- panel swap ---------------------------------------------------
       Luke 2026-09-14: moving between two tier-2 items flashed the page
       between the panels. The CSS above this in header-tabbed.css carries
       the measurement and the reasoning; the invariant this code keeps is:
       WHILE A SWITCH IS RUNNING, SOME PANEL IS AT OPACITY 1 UNDERNEATH THE
       ONE FADING IN, and both are clipped to the same height, so the page
       can never composite through. Nothing here queues: a second target
       retargets the same height tween from wherever it is and re-points the
       fade, so a sweep across five categories ends on the fifth with no
       backlog and no ghost. */
    var panels = Array.prototype.slice.call(nav.querySelectorAll('[data-hub-panel]'));
    var slowMo = window.matchMedia ? window.matchMedia('(prefers-reduced-motion:reduce)') : { matches: false };
    var underPanel = null, swapIn = null, swapRaf = 0, swapTimer = null, swapH = null;

    function opaque(p) { return !!p && parseFloat(getComputedStyle(p).opacity) > 0.99; }
    /* The panel's height cap = the space actually beneath it (Luke 2026-09-14:
       "the dropdown section should morph/transition into the height it needs
       to be"). The panels sit at top:100% of .hubnav, so the nav's own bottom
       IS their top — no assumption about how tall the header happens to be,
       which is what the old fixed 560px cap got wrong. Republished on load,
       on resize and on every open, because the nav's bottom moves with the
       pinned / collapsed / peeked scroll states. Floor of 240px so a tiny
       window still shows something scrollable rather than a sliver. */
    function capPanels() {
      var top = nav.getBoundingClientRect().bottom;
      var room = Math.round(Math.max(240, (window.innerHeight || 0) - top - 16));
      nav.style.setProperty('--hub-panel-max', room + 'px');
    }
    // the height the panel WANTS, read past the swap clamp (inline style beats
    // the .is-swapping rule); one forced layout per swap, never per frame
    function naturalH(p) {
      if (!p) return 0;
      var was = p.style.height;
      p.style.height = 'auto';
      var h = p.offsetHeight;
      p.style.height = was;
      return h;
    }
    /* Take a panel out of the swap stack without letting it fade FROM full
       opacity. Luke 2026-09-14, on this branch's preview theme, Tiles hub
       moving Bathroom -> Wall -> Floor: "when moving left-ways through the
       2nd level megamenu items the transitions flash twice, instead of once.
       Moving right-ways this doesn't seem to be an issue." That was this
       code's own bug: the held panel was released at opacity 1 and reverted
       to the base rule, which fades it 1 -> 0 over 160ms. Released panels and
       the open one both sat at z-index 49, so DOCUMENT ORDER decided who
       painted on top — and moving right-to-left the panel being released is
       later in the row, so the OLD menu reappeared at full opacity on top of
       the new one and then faded: a second flash, leftwards only.
       Two fixes, belt and braces: the open panel now holds z-index 50 for as
       long as it is open (CSS), so nothing can paint over it; and a released
       panel is hidden instantly here, so it never animates from 1 and never
       shows a taller panel's bottom edge past the new one. The class comes
       off two frames later, from opacity 0 to opacity 0 — no transition. */
    function hideNow(p) {
      p.classList.remove('is-swap-under');
      p.classList.remove('is-swap-in');
      p.classList.add('is-swap-done');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { p.classList.remove('is-swap-done'); });
      });
    }
    function release(p, target, hold) {
      if (p === target || p === hold) return;
      if (p.classList.contains('is-swap-under') || p.classList.contains('is-swap-in')) {
        if (parseFloat(getComputedStyle(p).opacity) > 0.01) { hideNow(p); return; }
      }
      p.classList.remove('is-swap-under');
      p.classList.remove('is-swap-in');
    }

    function dropClamp() {
      nav.classList.remove('is-swapping');
      nav.style.removeProperty('--hub-swap-h');
      swapH = null; swapTimer = null;
    }
    // `linger` keeps the height clamp for that long after the panels are
    // released — on a CLOSE, so the leaving panel fades at the height it was
    // showing instead of snapping back to its natural one first
    function endSwap(linger) {
      if (swapRaf) { cancelAnimationFrame(swapRaf); swapRaf = 0; }
      if (swapTimer) { clearTimeout(swapTimer); swapTimer = null; }
      for (var i = 0; i < panels.length; i++) release(panels[i], swapIn, null);
      if (swapIn) { swapIn.classList.remove('is-swap-in'); swapIn.classList.remove('is-swap-done'); }
      underPanel = null; swapIn = null;
      if (linger && nav.classList.contains('is-swapping')) swapTimer = setTimeout(dropClamp, linger);
      else if (nav.classList.contains('is-swapping')) dropClamp();
    }
    function tweenH(to) {
      if (swapRaf) { cancelAnimationFrame(swapRaf); swapRaf = 0; }
      if (swapH === null || Math.abs(to - swapH) < 1 || slowMo.matches) {
        swapH = to; nav.style.setProperty('--hub-swap-h', to + 'px'); return;
      }
      var from = swapH, t0 = 0;
      swapRaf = requestAnimationFrame(function step(now) {
        if (!t0) t0 = now;
        var k = Math.min(1, (now - t0) / SWAP_H);
        var e = 1 - Math.pow(1 - k, 3);               // ease-out cubic
        swapH = Math.round(from + (to - from) * e);
        nav.style.setProperty('--hub-swap-h', swapH + 'px');
        swapRaf = k < 1 ? requestAnimationFrame(step) : 0;
      });
    }
    /* Arrange the stack for a switch to `li`. Returns false when there is
       nothing on screen to hide behind — that is an OPEN, not a switch, and
       the plain fade-over-the-page is the right animation for it. Must run
       BEFORE is-open moves: it reads the leaving panel while it is still
       painted. */
    function swapTo(li, from) {
      var next = panelOf(li);
      if (!next) return false;
      capPanels();                       // heights are measured against it below
      var leaving = from ? panelOf(from) : null;
      var live = nav.classList.contains('is-swapping');
      // the arriving panel is already opaque (a swap BACK to the held panel):
      // it needs no floor, it only needs to be on top
      var hold = opaque(next) ? null
        : (leaving && leaving !== next && opaque(leaving)) ? leaving
        : (underPanel && underPanel !== next && opaque(underPanel)) ? underPanel : null;
      if (!hold && !opaque(next)) {
        // nothing to hide behind: a plain open. If a clamp is still lingering
        // from a close a moment ago, morph it to this panel instead of
        // dropping it under the shopper (re-open inside CLOSE_MS).
        if (live) {
          tweenH(naturalH(next));
          if (swapTimer) clearTimeout(swapTimer);
          swapTimer = setTimeout(function () { endSwap(0); }, SWAP_H + 60);
        }
        return false;
      }
      if (!live) {
        swapH = (hold || next).offsetHeight;
        nav.style.setProperty('--hub-swap-h', swapH + 'px');
        nav.classList.add('is-swapping');
      }
      for (var i = 0; i < panels.length; i++) {
        var p = panels[i];
        if (p === next) { p.classList.remove('is-swap-under'); p.classList.remove('is-swap-done'); p.classList.add('is-swap-in'); }
        else if (p === hold) { p.classList.remove('is-swap-in'); p.classList.remove('is-swap-done'); p.classList.add('is-swap-under'); }
        else release(p, next, hold);
      }
      underPanel = hold; swapIn = next;
      tweenH(naturalH(next));
      if (swapTimer) clearTimeout(swapTimer);
      swapTimer = setTimeout(function () { endSwap(0); }, Math.max(SWAP_IN, SWAP_H) + 60);
      return true;
    }

    function setOpen(li) {
      if (openCat === li) return;
      // reduced motion swaps instantly (CSS kills every panel transition), so
      // there is no translucent moment to cover and no tween to run
      if (li) capPanels();
      if (li && !slowMo.matches) swapTo(li, openCat);
      if (openCat) {
        openCat.classList.remove('is-open');
        var a0 = linkOf(openCat); if (a0) a0.setAttribute('aria-expanded', 'false');
      }
      openCat = li;
      if (li) {
        li.classList.add('is-open');
        var a1 = linkOf(li); if (a1) a1.setAttribute('aria-expanded', 'true');
      }
      // closing: release the panels now (they fade out together, which is
      // what a close should look like) but hold the height clamp until the
      // fade is over, so nothing snaps back to its natural height on the way
      if (!li) endSwap(CLOSE_MS);
      // theme.js's reveal-on-scroll reads this so the header never hides
      // under an open panel
      header.classList.toggle('is-menu-open', !!li);
      // a panel open under a hovered hub is a commitment to that hub
      if (li) remember(hubOf(li));
    }

    function showHub(key) {
      var band = null;
      tabs.forEach(function (t) {
        var on = t.getAttribute('data-hub-tab') === key;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      minis.forEach(function (m) { m.classList.toggle('is-active', m.getAttribute('data-hub-mini') === key); });
      bands.forEach(function (b) {
        var on = b.getAttribute('data-hub-band') === key;
        b.classList.toggle('is-active', on);
        b.hidden = !on;
        if (on) band = b;
      });
      // a panel open in the band we just left must not stay open
      if (openCat && (!band || !band.contains(openCat))) setOpen(null);
      // the scroll controller (3.) peeks a collapsed band on this
      nav.dispatchEvent(new CustomEvent('hubnav:show'));
    }
    function leave() { nav.dispatchEvent(new CustomEvent('hubnav:leave')); }

    capPanels();
    window.addEventListener('resize', function () { capPanels(); }, { passive: true });

    minis.forEach(function (m) {
      var key = m.getAttribute('data-hub-mini');
      m.addEventListener('pointerenter', function () {
        clearTimeout(tabTimer);
        tabTimer = setTimeout(function () { showHub(key); }, TAB_DELAY);
      });
      m.addEventListener('pointerleave', function () { clearTimeout(tabTimer); });
      m.addEventListener('focus', function () { showHub(key); });
    });

    tabs.forEach(function (t, i) {
      var key = t.getAttribute('data-hub-tab');
      t.addEventListener('pointerenter', function () {
        clearTimeout(tabTimer);
        tabTimer = setTimeout(function () { showHub(key); }, TAB_DELAY);
      });
      t.addEventListener('pointerleave', function () { clearTimeout(tabTimer); });
      t.addEventListener('focus', function () { showHub(key); });
      t.addEventListener('keydown', function (e) {
        var n = null;
        if (e.key === 'ArrowRight') n = tabs[(i + 1) % tabs.length];
        else if (e.key === 'ArrowLeft') n = tabs[(i - 1 + tabs.length) % tabs.length];
        else if (e.key === 'Home') n = tabs[0];
        else if (e.key === 'End') n = tabs[tabs.length - 1];
        else if (e.key === 'ArrowDown') {
          var band = activeBand();
          var first = band && band.querySelector('.hubnav__cat-link');
          if (first) { e.preventDefault(); first.focus(); }
          return;
        }
        if (n) { e.preventDefault(); n.focus(); }
      });
    });

    cats.forEach(function (li) {
      var has = !!panelOf(li);
      var a = linkOf(li);
      li.addEventListener('pointerenter', function () {
        clearTimeout(closeTimer);
        if (coarse.matches) return;
        if (!openCat) {
          if (has) openTimer = setTimeout(function () { setOpen(li); }, OPEN_DELAY);
          return;
        }
        if (openCat === li) { clearTimeout(switchTimer); return; }
        clearTimeout(switchTimer);
        switchTimer = setTimeout(function () {
          if (li.matches(':hover')) setOpen(has ? li : null);
        }, SETTLE);
      });
      li.addEventListener('pointerleave', function () { clearTimeout(openTimer); });
      if (!a) return;
      a.addEventListener('focus', function () { if (has) setOpen(li); else setOpen(null); });
      a.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown' && has) {
          e.preventDefault();
          setOpen(li);
          var first = panelOf(li).querySelector('a');
          if (first) first.focus();
        }
      });
    });

    header.addEventListener('pointerleave', function () {
      clearTimeout(switchTimer); clearTimeout(tabTimer); clearTimeout(openTimer);
      closeTimer = setTimeout(function () { setOpen(null); returnHome(); leave(); }, CLOSE_GRACE);
    });
    header.addEventListener('pointerenter', function () { clearTimeout(closeTimer); });
    // keyboard users tabbing OUT of the nav take the panel (and the peeked
    // band) with them; tabbing INTO a collapsed band peeks it
    nav.addEventListener('focusout', function (e) {
      if (!e.relatedTarget || !nav.contains(e.relatedTarget)) { setOpen(null); returnHome(); leave(); }
    });
    nav.addEventListener('focusin', function (e) {
      if (e.target.closest && e.target.closest('[data-hub-band]')) nav.dispatchEvent(new CustomEvent('hubnav:show'));
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (openCat) {
        var back = linkOf(openCat);
        setOpen(null);
        if (back && nav.contains(document.activeElement)) back.focus();
      }
      // Escape also closes a peeked band, returning focus to its tab
      var peekTab = nav.classList.contains('is-peek') && nav.contains(document.activeElement) ? nav.querySelector('.hubnav__tab.is-active, .hubnav__cond-tab.is-active') : null;
      leave();
      if (peekTab && peekTab.offsetParent) peekTab.focus({ preventScroll: true });
    });
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var catLink = e.target.closest('.hubnav__cat-link');
      if (catLink && coarse.matches) {
        var li = catLink.closest('[data-hub-cat]');
        if (li && panelOf(li) && openCat !== li) { e.preventDefault(); setOpen(li); return; }
      }
      var navLink = e.target.closest('.hubnav a');
      if (navLink) { remember(hubOf(navLink)); setOpen(null); return; }
      if (openCat && !nav.contains(e.target)) setOpen(null);
    }, true);
  }

  /* ---------- 2. deliver-to chip ---------------------------------------- */
  var deliver = header.querySelector('[data-deliver]');
  if (deliver) {
    var KEY = 'st.postcode';
    var toggle = deliver.querySelector('[data-deliver-toggle]');
    var pop = deliver.querySelector('[data-deliver-pop]');
    var input = deliver.querySelector('[data-deliver-input]');
    var list = deliver.querySelector('[data-locality-list]');
    var value = deliver.querySelector('[data-deliver-value]');
    var clearBtn = deliver.querySelector('[data-deliver-clear]');
    var emptyLabel = value ? (value.getAttribute('data-empty-label') || 'Set your postcode') : '';
    // assets/locality.js (loaded before this file by header.liquid) owns
    // st.locality + the typeahead; everything here tolerates its absence
    var LOC = function () { return window.SydneyTaps && window.SydneyTaps.locality; };

    function read() { try { return (localStorage.getItem(KEY) || '').trim(); } catch (e) { return ''; } }
    // the suburb only counts while it agrees with the postcode
    function readLoc() { var L = LOC(); var loc = L ? L.get() : null; return loc && loc.postcode === read() ? loc : null; }
    // "Lane Cove 2064" for the chip, "Lane Cove NSW 2064" back in the input, "2064" with no suburb
    function place(pc, loc, withState) {
      if (!loc) return pc;
      return loc.suburb + (withState && loc.state ? ' ' + loc.state : '') + ' ' + pc;
    }
    // COMPARE BEFORE WRITING. snippets/locality-boot.liquid has already
    // painted this label from the same record, synchronously, before first
    // paint (Luke 2026-09-15: the chip must not flash the prompt on every
    // navigation). An unconditional textContent write here would be a second
    // paint of an identical string — harmless to layout, but it is precisely
    // the write that used to arrive ~370 ms after FCP, so the guard keeps the
    // boot path provably a no-op and any future regression visible in the
    // cls-gate chip probe.
    function paint(pc) {
      var t = pc ? place(pc, readLoc()) : emptyLabel;
      if (value && value.textContent !== t) value.textContent = t;
      if (clearBtn && clearBtn.hidden === !!pc) clearBtn.hidden = !pc;
      deliver.classList.toggle('is-set', !!pc);
    }
    function announce(pc) {
      document.dispatchEvent(new CustomEvent('st:postcode', { detail: { postcode: pc || null, locality: pc ? readLoc() : null } }));
    }
    function set(pc, loc) {
      pc = String(pc || '').replace(/\D/g, '').slice(0, 4);
      if (pc.length !== 4) return false;
      try { localStorage.setItem(KEY, pc); } catch (e) { /* private mode: chip still paints */ }
      var L = LOC();
      if (L) L.set(loc && String(loc.postcode) === pc ? loc : null);
      // keep the pre-paint cache (locality-boot.liquid) in step, so a second
      // render of that snippet later in the document paints the NEW place
      window.SydneyTaps.__place = { pc: pc, loc: readLoc() };
      paint(pc); announce(pc);
      return true;
    }
    function clear() {
      try { localStorage.removeItem(KEY); } catch (e) { /* ignore */ }
      var L = LOC();
      if (L) L.set(null);
      window.SydneyTaps.__place = { pc: '', loc: null };
      paint(''); announce('');
    }
    function openPop() {
      pop.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      if (input) {
        var pc = read();
        input.value = pc ? place(pc, readLoc(), true) : '';
        setTimeout(function () { input.focus(); input.select(); }, 30);
      }
    }
    function closePop() {
      if (field) field.close();
      pop.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
    }

    window.SydneyTaps = window.SydneyTaps || {};
    window.SydneyTaps.postcode = { get: read, set: set, clear: clear, locality: readLoc };

    var initial = read();
    paint(initial);
    // hand a stored place to the PDP estimate field when it is empty
    var eta = document.querySelector('[data-postcode]');
    if (initial && eta && !eta.value) eta.value = place(initial, readLoc(), true);

    var field = input && list && LOC()
      ? LOC().attach(input, { list: list, onSelect: function (loc) { set(loc.postcode, loc); closePop(); toggle.focus(); } })
      : null;
    function invalid() {
      if (!input) return;
      input.setCustomValidity(field ? 'Choose a suburb from the list or enter a 4-digit postcode' : 'Enter a 4-digit Australian postcode');
      input.reportValidity(); input.setCustomValidity('');
    }
    toggle.addEventListener('click', function () { if (pop.hidden) openPop(); else closePop(); });
    pop.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!field) { if (set(input.value)) closePop(); else invalid(); return; }
      field.resolve().then(function (r) {
        if (!r) { invalid(); return; }
        if (r.locality) return;                 // onSelect already stored it and closed the popover
        if (set(r.postcode)) closePop(); else invalid();
      });
    });
    if (clearBtn) clearBtn.addEventListener('click', function () { clear(); closePop(); toggle.focus(); });
    document.addEventListener('click', function (e) {
      if (!pop.hidden && !deliver.contains(e.target)) closePop();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !pop.hidden) { closePop(); toggle.focus(); }
    });
  }

  /* ---------- 3. scroll behaviour ------------------------------------- */
  var root = document.documentElement;
  var mode = root.getAttribute('data-hdr-mode');
  if (mode && mode !== 'hide-all') (function () {
    var topbar = header.querySelector('.topbar');
    var bar = header.querySelector('.site-header__bar');
    var strip = nav ? nav.querySelector('.hubnav__strip') : null;
    var cond = nav ? nav.querySelector('[data-hdr-cond]') : null;
    var desktop = window.matchMedia('(min-width:901px)');
    var revealMode = root.getAttribute('data-header-scroll') === 'reveal';
    var UP_DIST = 120, UP_VEL = 1.2, DOWN_DIST = 24, DELTA = 4, FLOOR = 120;
    var topH = 0, off = 0, restH = 0, lastH = 0;
    var pinned = false, revealed = false, revealTop = false;
    var lastY = window.scrollY, lastT = 0, upAcc = 0, downAcc = 0, ticking = false, primed = false;
    var sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:0;pointer-events:none;visibility:hidden';
    document.body.insertBefore(sentinel, document.body.firstChild);

    function bandH() {
      var b = nav && nav.querySelector('[data-hub-band]:not([hidden])');
      var ul = b && b.firstElementChild;
      return ul ? ul.offsetHeight : 48;
    }
    function frozen() {
      return document.body.classList.contains('drawer-open')
        || header.classList.contains('is-searching') || root.classList.contains('is-searching')
        || header.classList.contains('is-menu-open') || header.classList.contains('is-search-open')
        || (nav && nav.classList.contains('is-peek'))
        || !!header.querySelector('[data-flyout]:not([hidden])')
        || (bar && bar.contains(document.activeElement))
        || (topbar && topbar.contains(document.activeElement));
    }
    // --header-h = what actually overlays the page: the rest height when the
    // header is in flow, else the sum of the pinned rows
    function publish() {
      var h;
      if (!desktop.matches || !pinned) {
        h = Math.round(header.getBoundingClientRect().height);
      } else {
        h = 0;
        if (revealTop && topbar) h += topbar.offsetHeight;
        if (revealed && bar) h += bar.offsetHeight;
        if (mode === 'condensed' && !revealed) h += cond ? cond.offsetHeight : 0;
        else h += strip ? strip.offsetHeight : 0;
        if (mode === 'tabs-band-sticky' || (mode === 'condensed' && revealed)) h += bandH();
      }
      if (!h || h === lastH) return;
      lastH = h;
      root.style.setProperty('--header-h', h + 'px');
      // ui.js recomputes --rail-top on resize only (see theme.js)
      window.dispatchEvent(new Event('resize'));
    }
    function measureRest() {
      var hr = header.getBoundingClientRect();
      topH = topbar ? Math.round(topbar.getBoundingClientRect().height) : 0;
      off = bar ? Math.round(bar.getBoundingClientRect().bottom - hr.top) : 0;
      if (!pinned) {
        restH = Math.round(hr.height);
        header.style.setProperty('--hdr-rest-h', restH + 'px');
      }
      sentinel.style.height = off + 'px';
    }
    function apply() {
      var d = desktop.matches;
      var p = d && pinned, r = p && revealed;
      header.classList.toggle('is-pinned', p);
      header.classList.toggle('is-revealed', r);
      header.classList.toggle('is-compact', p && mode !== 'condensed');
      header.classList.toggle('is-cond', p && !r && mode === 'condensed');
      if (nav) {
        var collapsed = p && (mode === 'tabs-sticky' || (mode === 'condensed' && !r));
        nav.classList.toggle('is-collapsed', collapsed);
        if (!collapsed) nav.classList.remove('is-peek');
      }
      var offset = !d ? 0 : (p ? (r ? (revealTop ? 0 : topH) : off) : off);
      root.style.setProperty('--hdr-off', offset + 'px');
      if (d) root.classList.remove('header-hidden');
      publish();
    }
    function setPinned(on) {
      if (pinned === on) return;
      pinned = on; revealed = false; revealTop = false; upAcc = downAcc = 0;
      apply();
    }
    function reveal(on, top) {
      if (revealed === on && revealTop === !!top) return;
      revealed = on; revealTop = on && !!top;
      apply();
    }

    // the sentinel is exactly topbar + bar tall: it leaves the viewport the
    // moment the strip reaches the top, whatever scrolled the page
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        setPinned(!entries[entries.length - 1].isIntersecting);
      }, { threshold: 0 }).observe(sentinel);
    } else {
      setPinned(window.scrollY >= off);
    }

    function mobileScroll(y, diff) {
      // <=900px: today's reveal behaviour, run from here because theme.js stood down
      if (!revealMode) return;
      if (Math.abs(diff) < 6 || Math.abs(diff) > window.innerHeight * 0.5) return;
      if (y <= FLOOR || frozen()) root.classList.remove('header-hidden');
      else if (diff > 0) root.classList.add('header-hidden');
      else root.classList.remove('header-hidden');
    }
    function onScroll() {
      ticking = false;
      var y = window.scrollY, t = performance.now();
      var diff = y - lastY, dt = (t - lastT) || 16;
      lastT = t;
      if (!primed) { primed = true; lastY = y; return; }
      if (!desktop.matches) { mobileScroll(y, diff); lastY = y; return; }
      if (Math.abs(diff) < DELTA) return;
      lastY = y;
      if (!('IntersectionObserver' in window)) setPinned(y >= off);
      if (Math.abs(diff) > window.innerHeight * 0.5) { upAcc = downAcc = 0; return; }
      if (!pinned) return;
      if (diff > 0 && nav && nav.classList.contains('is-peek') && !header.classList.contains('is-menu-open')) nav.classList.remove('is-peek');
      if (frozen()) return;
      if (diff < 0) {
        upAcc += -diff; downAcc = 0;
        if (!revealed && (upAcc > UP_DIST || (-diff / dt) > UP_VEL)) reveal(true);
      } else {
        downAcc += diff; upAcc = 0;
        if (revealed && downAcc > DOWN_DIST) reveal(false);
      }
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(onScroll);
    }, { passive: true });

    if (nav) {
      nav.addEventListener('hubnav:show', function () { if (nav.classList.contains('is-collapsed')) nav.classList.add('is-peek'); });
      nav.addEventListener('hubnav:leave', function () { nav.classList.remove('is-peek'); });
      var condSearch = nav.querySelector('[data-cond-search]');
      if (condSearch) condSearch.addEventListener('click', function () {
        // push, never cover: the real bar slides in above the row and the
        // static input takes focus once it has arrived
        reveal(true);
        var input = header.querySelector('[data-usearch-slot]:not([hidden]) input, [data-usearch-input]');
        // preventScroll: focusing while the wrapper's top is still animating
        // made Chrome scroll the page to the top (harness, 2026-09-08)
        if (input) setTimeout(function () { input.focus({ preventScroll: true }); }, 320);
      });
    }
    // a hidden bar must never hold focus (Tab from the page, the "/" shortcut)
    document.addEventListener('focusin', function (e) {
      if (!pinned || !desktop.matches) return;
      if (topbar && topbar.contains(e.target)) reveal(true, true);
      else if (bar && bar.contains(e.target)) reveal(true);
    });
    window.addEventListener('pageshow', function () {
      lastY = window.scrollY; primed = false;
      root.classList.remove('header-hidden');
      measureRest();
      setPinned(desktop.matches && window.scrollY >= off);
    });
    var resizeTimer = null;
    window.addEventListener('resize', function (e) {
      if (!e.isTrusted) return; // our own publish() dispatches resize
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { measureRest(); apply(); }, 120);
    });
    if (desktop.addEventListener) desktop.addEventListener('change', function () {
      root.classList.remove('header-hidden');
      measureRest(); apply();
    });

    measureRest();
    apply();
    window.addEventListener('load', function () { measureRest(); publish(); });
  })();
})();
